import {
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest
}                                       from '@angular/common/http';
import { Injectable }                   from '@angular/core';
import { AnalyticsDataProviderService } from '@core/analytics/analytics-data-provider.service';
import {
	IXClientAnalyticsConfig,
	X_CLIENT_ANALYTICS_CONFIG
}                                       from '@interceptors/config/x-client-analytics-header-config';
import {
	HEADER_CLIENT_ANALYTICS_INTERCEPTOR,
	IHeaderClientAnalyticsInterceptor
}                                       from '@interceptors/interceptors.model';
import {
	IAnalyticsHeaderComplex,
	IAnalyticsHeaderSingle
}                                       from '@interceptors/model/x-client-analytics-header-model';
import { LoggerService }                from '@shared/services/logger.service';
import {
	ObjectUtils,
	StringUtils
}                                       from '@utils/helpers';
import { Observable }                   from 'rxjs';

/**
 * Interceptor for adding analytics header(s) to the request. Values are cached as much as possible - which means that
 * static values like OS are obtained once and dynamic values like screen resolution are obtained on every resize event and written once to local fields.
 * Caching is done via local fields and session storage (determines whether some change happened).
 */
@Injectable()
export class XClientAnalyticsInterceptor implements HttpInterceptor {
	// Config
	private readonly _xClientAnalyticsConfig: IXClientAnalyticsConfig = X_CLIENT_ANALYTICS_CONFIG;

	constructor(
		private readonly _analyticsDataProviderService: AnalyticsDataProviderService,
		private readonly _logger: LoggerService
	) {	}

	intercept( req: HttpRequest<any>, next: HttpHandler ): Observable<HttpEvent<any>> {
		if ( !this._xClientAnalyticsConfig.featureEnabled ) { return next.handle( req ); }

		const headerClientAnalyticsInterceptor: IHeaderClientAnalyticsInterceptor = req.context.get( HEADER_CLIENT_ANALYTICS_INTERCEPTOR );

		if ( headerClientAnalyticsInterceptor.enabled ) {
			req = this.updateHeader( req );
		} else { // Todo keep this as long all api calls are not refactored to support HttpContext
			req = this.updateHeader( req );
		}

		this._logger.trace( 'XClientAnalyticsInterceptor:intercept->req', req );
		return next.handle( req );
	}

	/**
	 * Update header is needed and only for api calls.
	 */
	private updateHeader( req: HttpRequest<any> ): HttpRequest<any> {
		// stop processing on request for local resources
		if ( !req.url.match( '\/.*api.*\/' ) ) { return req; }

		const analyticsHeaderComplex: IAnalyticsHeaderComplex = this.populateAnalyticsData();

		// checks for AnalyticsData changes and returns req object without additional headers if there is no change
		if ( this.areAnalyticsDataEqual( analyticsHeaderComplex ) ) {
			return req;
		}

		// update localSession data
		this.writeLocalSessionData( analyticsHeaderComplex );

		// adds additional analytics header to req object
		// complex
		if ( this._xClientAnalyticsConfig.addComplexHeader ) {
			req = req.clone( { headers: req.headers.set( this._xClientAnalyticsConfig.complexHeaderName, JSON.stringify( analyticsHeaderComplex ) ) } );
		}

		// simple
		if ( this._xClientAnalyticsConfig.addSingleHeaders ) {
			this.getStringSimpleData( analyticsHeaderComplex ).forEach( ( analyticsHeaderSingle: IAnalyticsHeaderSingle ) => {
				req = req.clone( {
					headers: req.headers.set(
						`${ this._xClientAnalyticsConfig.complexHeaderName }-${ analyticsHeaderSingle.headerName }`,
						analyticsHeaderSingle.headerValue
					)
				} );
			} );
		}

		return req;
	}

	/**
	 * Writes to the session the complex object.
	 */
	private writeLocalSessionData( analyticsHeaderComplex: IAnalyticsHeaderComplex ): void {
		window.localStorage.setItem( this._xClientAnalyticsConfig.sessionStorageId, JSON.stringify( analyticsHeaderComplex ) );
	}

	/**
	 * Reads from the session the complex object
	 */
	private readLocalSessionData(): IAnalyticsHeaderComplex {
		const analyticsHeaderComplexString: string = window.localStorage.getItem( this._xClientAnalyticsConfig.sessionStorageId );
		return analyticsHeaderComplexString
					 ? JSON.parse( analyticsHeaderComplexString ) as IAnalyticsHeaderComplex
					 : null;
	}

	/**
	 * Compares complex data with the session stored ones.
	 */
	private areAnalyticsDataEqual( analyticsHeaderComplex: IAnalyticsHeaderComplex ): boolean {
		return ObjectUtils.simpleIsEqual( analyticsHeaderComplex, this.readLocalSessionData() );
	}

	/**
	 * Reads data by "cached" get methods.
	 */
	private populateAnalyticsData(): IAnalyticsHeaderComplex {
		const analyticsHeaderComplex: IAnalyticsHeaderComplex =
			{
				browserNameVersion: this._analyticsDataProviderService.getBrowserNameVersion(),
				deviceOrientation: this._analyticsDataProviderService.getDeviceOrientation(),
				displayDensity: this._analyticsDataProviderService.getDisplayDensity(),
				dpi: this._analyticsDataProviderService.getDpi(),
				isTouchDevice: this._analyticsDataProviderService.getIsTouchDevice(),
				platform: this._analyticsDataProviderService.getPlatform(),
				screenResolution: this._analyticsDataProviderService.getScreenResolution(),
				windowResolution: this._analyticsDataProviderService.getWindowResolution(),
				zoomLevel: this._analyticsDataProviderService.getZoomLevel()
			};

		this._logger.debug( 'analyticsHeaderComplex', analyticsHeaderComplex );
		return analyticsHeaderComplex;
	}

	/**
	 * Converts complex data to simple string array one by one.
	 */
	private getStringSimpleData( analyticsHeaderComplex: IAnalyticsHeaderComplex ): IAnalyticsHeaderSingle[] {
		const analyticsHeaderSingle: IAnalyticsHeaderSingle[] = [];

		Object.entries( analyticsHeaderComplex ).forEach( ( [ headerName, headerValue ]: [ string, any ] ) => {
			analyticsHeaderSingle.push( { headerName: StringUtils.capitalizeAtPositions( headerName, [ 0 ] ), headerValue: JSON.stringify( headerValue ) } );
		} );

		return analyticsHeaderSingle;
	}

}
